import type { Control, FieldErrors, FieldValues } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { ConfirmationCheckbox } from '@/components/form/ConfirmationCheckbox';
import { InquiryGuidanceIntro } from './InquiryGuidance';
import { SurveyCommentQuestion } from './SurveyCommentQuestion';
import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { getSurveyConsentFieldName, type SurveyElement } from '../../model/surveySchema';

export const SURVEY_INQUIRY_DISCLOSURE =
  'Primum submits your question anonymously to a relevant expert, and any reply arrives in your Primum inbox.';

interface SurveyInquiryQuestionProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  errorMessage?: string;
}

/**
 * Renders a `comment` element carrying an enabled Primum inquiry marker — a
 * survey question whose answer the backend turns into a real inquiry.
 *
 * The client submits this like any other answer and does no inquiry writing of
 * its own; the card and acknowledgement exist because the consequence differs
 * from every other question on the page. The acknowledgement is both a gate on
 * submitting and the respondent's attestation of record: it travels with the
 * answer as its `noPII` value, and the server submits the inquiry as attesting
 * to nothing when it is absent.
 *
 * The disclosure sits between the question and the input rather than at the top
 * of the card, so it reads in the order it applies: what is being asked, then
 * what answering it does, then the field. It stays above the input either way,
 * so a screen-reader or keyboard user still hears what happens to their answer
 * before writing it, and it is wired to the field as a description so it is
 * announced as part of it.
 *
 * Take the disclosure wording as a product decision rather than a description
 * of the backend, which routes to Primum for triage with no expert selected and
 * creates no inquiry at all for a guest whose email matches no account. It
 * names the anonymous routing explicitly so the respondent knows before they
 * answer. Keep it in sync with the test that pins it.
 */
export function SurveyInquiryQuestion({
  element,
  control,
  errors,
  errorMessage,
}: SurveyInquiryQuestionProps) {
  const disclosureId = `${element.name}-inquiry-disclosure`;

  const guidance = element.primumInquiry?.guidance;

  return (
    <Card
      variant="outline"
      size="md"
      className={twJoin(
        'border-outline-500',
        guidance ? 'gap-4 bg-background-0' : 'gap-3 bg-background-50'
      )}>
      {guidance?.stepLabel ? (
        <Text size="xs" bold className="uppercase tracking-wide text-primary-600">
          {guidance.stepLabel}
        </Text>
      ) : null}

      <SurveyCommentQuestion
        element={element}
        control={control}
        errorMessage={errorMessage}
        placeholder={guidance?.placeholder}
        emphasized={Boolean(guidance)}
        description={
          <>
            {guidance ? <InquiryGuidanceIntro guidance={guidance} /> : null}
            <Text size="sm" nativeID={disclosureId} className="text-typography-700">
              {SURVEY_INQUIRY_DISCLOSURE}
            </Text>
          </>
        }
        describedById={disclosureId}
        accessibilityHint={SURVEY_INQUIRY_DISCLOSURE}
      />

      <ConfirmationCheckbox
        variant="pii"
        control={control}
        errors={errors}
        fieldName={getSurveyConsentFieldName(element.name)}
      />
    </Card>
  );
}
